export const TRAFFIC_PRESETS = [
  'TODAY',
  'YESTERDAY',
  'LAST_7_DAYS',
  'LAST_30_DAYS',
  'THIS_MONTH',
  'LAST_MONTH',
  'THIS_YEAR',
  'LAST_YEAR',
  'CUSTOM'
] as const

export type TrafficPreset = (typeof TRAFFIC_PRESETS)[number]
export type TrafficGranularity = 'HOUR' | 'DAY' | 'MONTH'
export type TrafficDataState = 'READY' | 'EMPTY' | 'UNAVAILABLE'
export type TrafficFreshnessState = 'CURRENT' | 'STALE' | 'REBUILDING' | 'UNKNOWN'

export interface TrafficPeriodSelection {
  preset: TrafficPreset
  from?: string
  until?: string
}

export interface TrafficPeriodView {
  preset: TrafficPreset
  from: string
  until: string
  granularity: TrafficGranularity
}

export interface TrafficMetricSummary {
  pv: number | null
  uv: number | null
  newVisitors: number | null
  sessions: number | null
  averageActiveMillis: number | null
  bounceRate: number | null
}

export interface TrafficTrendPoint {
  bucket: string
  pv: number | null
  uv: number | null
  newRegisteredUsers: number | null
}

export interface TrafficRankingRow {
  code: string
  name: string
  pv: number | null
  uv: number | null
  averageActiveMillis: number | null
}

export interface TrafficUserAssets {
  registeredUserTotal: number | null
  newRegisteredUsers: number | null
  activeRegisteredUsers: number | null
}

export interface TrafficWorkbenchBase {
  period: TrafficPeriodView
  trafficSummary: TrafficMetricSummary
  trend: TrafficTrendPoint[]
  dataState: TrafficDataState
  freshnessState: TrafficFreshnessState
  refreshedAt: string | null
  /** 指标定义版本，随后端 TrafficWorkbenchService.METRIC_DEFINITION_REF 升级（当前 v2）。 */
  metricDefinitionRef: 'tenant-traffic-v2'
}

export interface ShellTrafficOverview extends TrafficWorkbenchBase {
  userAssets: TrafficUserAssets
  moduleRanking: TrafficRankingRow[]
}

export interface ModuleTrafficOverview extends TrafficWorkbenchBase {
  moduleCode: string
  topPages: TrafficRankingRow[]
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const DAY_MILLIS = 24 * 60 * 60 * 1000
const MAX_CUSTOM_DAYS = 366

export function buildTrafficPeriodQuery(period: TrafficPeriodSelection): string {
  const query = new URLSearchParams({ preset: period.preset })
  if (period.preset !== 'CUSTOM') {
    return query.toString()
  }

  if (!period.from || !period.until) {
    throw new Error('CUSTOM traffic period requires both from and until dates')
  }

  const fromMillis = parseIsoDate(period.from, 'from')
  const untilMillis = parseIsoDate(period.until, 'until')
  const days = (untilMillis - fromMillis) / DAY_MILLIS
  if (days <= 0) {
    throw new Error('CUSTOM traffic period until must be after from')
  }
  if (days > MAX_CUSTOM_DAYS) {
    throw new Error(`CUSTOM traffic period cannot exceed ${MAX_CUSTOM_DAYS} days`)
  }

  query.set('from', period.from)
  query.set('until', period.until)
  return query.toString()
}

export function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null || !Number.isFinite(milliseconds) || milliseconds < 0) return '-'

  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const parts: string[] = []
  if (hours > 0) parts.push(`${hours}小时`)
  if (minutes > 0) parts.push(`${minutes}分`)
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}秒`)
  return parts.join(' ')
}

export function formatPercent(value: number | null): string {
  return value === null || !Number.isFinite(value) ? '-' : `${(value * 100).toFixed(1)}%`
}

function parseIsoDate(value: string, field: 'from' | 'until'): number {
  const match = ISO_DATE.exec(value)
  if (!match) {
    throw new Error(`CUSTOM traffic period ${field} must use YYYY-MM-DD`)
  }

  const timestamp = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  if (new Date(timestamp).toISOString().slice(0, 10) !== value) {
    throw new Error(`CUSTOM traffic period ${field} must be a valid date`)
  }
  return timestamp
}
